/**
 * Os PONTOS DE ATENÇÃO — sem expressão de programador (mig 577).
 *
 * O Matheus, vendo a versão anterior: *"em gatilho está confuso para mim.
 * Sou leigo e dessa forma eu não sei mexer."* E o pedido: criar pontos sobre
 * questionários NOVOS do Auvo sem precisar de rodada.
 *
 * A resposta: ele ESCOLHE a pergunta numa lista (lida das tarefas reais),
 * diz quando o ponto aparece e escreve o rótulo. A expressão continua
 * existindo — atrás do "modo avançado", nunca como a primeira coisa da tela.
 * A prévia aplica o ponto às tarefas reais ANTES de salvar; "apareceria em
 * 0" é o aviso mais útil que existe.
 */
import { useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input, Select, Switch, useConfirmDialog } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

interface Ponto {
  id: string;
  ordem: number;
  rotulo: string;
  gatilhoRegex: string;
  fonte: 'questionario' | 'relato';
  palavrasChave: string[];
  ligado: boolean;
  perguntaTexto: string | null;
  perguntaId: number | null;
  quando: 'qualquer' | 'sim' | 'contem';
  contemTexto: string | null;
  mostrarResposta: boolean;
}

interface Pergunta {
  id: number | null;
  texto: string;
  tarefas: number;
  tarefas30d: number;
  tipos: string[];
  ultimaVezIso: string | null;
}

interface Previa {
  tarefasNoEspelho: number;
  dispararia: number;
  exemplo: { taskId: string; linha: string } | null;
}

type Tipo = 'pergunta' | 'relato' | 'avancado';

interface Rascunho {
  id: string | null; // null = criando
  tipo: Tipo;
  rotulo: string;
  perguntaTexto: string;
  perguntaId: number | null;
  quando: 'qualquer' | 'sim' | 'contem';
  contemTexto: string;
  mostrarResposta: boolean;
  palavras: string;
  gatilhoRegex: string;
  ligado: boolean;
}

const RASCUNHO_VAZIO: Rascunho = {
  id: null,
  tipo: 'pergunta',
  rotulo: '',
  perguntaTexto: '',
  perguntaId: null,
  quando: 'sim',
  contemTexto: '',
  mostrarResposta: false,
  palavras: '',
  gatilhoRegex: '',
  ligado: true,
};

/** O tipo que a tela mostra para um ponto guardado. */
function tipoDoPonto(p: Ponto): Tipo {
  if (p.perguntaTexto) return 'pergunta';
  if (p.fonte === 'relato') return 'relato';
  return 'avancado';
}

/** A frase da lista — o ponto descrito em português, não em colunas. */
function fraseDoPonto(p: Ponto): string {
  const t = tipoDoPonto(p);
  if (t === 'pergunta') {
    const quando =
      p.quando === 'qualquer'
        ? 'for respondida (qualquer resposta)'
        : p.quando === 'contem'
          ? `for respondida com “${p.contemTexto ?? ''}”`
          : 'for respondida “Sim”';
    return `Quando “${p.perguntaTexto}” ${quando}`;
  }
  if (t === 'relato') return `Quando o relato do técnico mencionar: ${p.palavrasChave.join(', ')}`;
  return `Modo avançado — expressão: ${p.gatilhoRegex}`;
}

function corpoDoRascunho(r: Rascunho) {
  return {
    tipo: r.tipo,
    rotulo: r.rotulo.trim(),
    perguntaTexto: r.tipo === 'pergunta' ? r.perguntaTexto : undefined,
    perguntaId: r.tipo === 'pergunta' ? r.perguntaId : undefined,
    quando: r.quando,
    contemTexto: r.quando === 'contem' ? r.contemTexto.trim() : undefined,
    mostrarResposta: r.mostrarResposta,
    palavrasChave:
      r.tipo === 'relato'
        ? r.palavras.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
    gatilhoRegex: r.tipo === 'avancado' ? r.gatilhoRegex.trim() : undefined,
    ligado: r.ligado,
  };
}

function prontoParaSalvar(r: Rascunho): boolean {
  if (!r.rotulo.trim()) return false;
  if (r.tipo === 'pergunta') {
    if (!r.perguntaTexto) return false;
    if (r.quando === 'contem' && !r.contemTexto.trim()) return false;
  }
  if (r.tipo === 'relato' && !r.palavras.split(',').some((s) => s.trim())) return false;
  if (r.tipo === 'avancado' && !r.gatilhoRegex.trim()) return false;
  return true;
}

export function PontosDeAtencaoCard() {
  const confirmar = useConfirmDialog();
  const [pontos, setPontos] = useState<Ponto[] | null>(null);
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [buscaPergunta, setBuscaPergunta] = useState('');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [avancadoAberto, setAvancadoAberto] = useState(false);

  useEffect(() => {
    api
      .get<{ pontos: Ponto[] }>('/api/v1/admin/whatsapp/pontos-atencao')
      .then((r) => setPontos(r.pontos))
      .catch(() => setErro('Não foi possível carregar os pontos de atenção.'));
    api
      .get<{ perguntas: Pergunta[] }>('/api/v1/admin/whatsapp/perguntas-do-questionario')
      .then((r) => setPerguntas(r.perguntas))
      .catch(() => {
        /* a lista chega no retry do próximo abrir */
      });
  }, []);

  const perguntasFiltradas = useMemo(() => {
    const b = buscaPergunta.trim().toLowerCase();
    if (!b) return perguntas;
    return perguntas.filter((p) => p.texto.toLowerCase().includes(b));
  }, [perguntas, buscaPergunta]);

  const abrirEditor = (p?: Ponto) => {
    setErro(null);
    setPrevia(null);
    setBuscaPergunta('');
    if (!p) {
      setRascunho({ ...RASCUNHO_VAZIO });
      setAvancadoAberto(false);
      return;
    }
    const tipo = tipoDoPonto(p);
    setRascunho({
      id: p.id,
      tipo,
      rotulo: p.rotulo,
      perguntaTexto: p.perguntaTexto ?? '',
      perguntaId: p.perguntaId,
      quando: p.quando,
      contemTexto: p.contemTexto ?? '',
      mostrarResposta: p.mostrarResposta,
      palavras: p.palavrasChave.join(', '),
      gatilhoRegex: p.gatilhoRegex,
      ligado: p.ligado,
    });
    setAvancadoAberto(tipo === 'avancado');
  };

  const verPrevia = async () => {
    if (!rascunho) return;
    setErro(null);
    setCarregandoPrevia(true);
    setPrevia(null);
    try {
      setPrevia(await api.post<Previa>('/api/v1/admin/whatsapp/pontos-atencao/previa', corpoDoRascunho(rascunho)));
    } catch (e) {
      const corpo = e instanceof ApiError ? (e.response as { erro?: string } | undefined) : undefined;
      setErro(corpo?.erro ?? 'Não foi possível montar a prévia.');
    } finally {
      setCarregandoPrevia(false);
    }
  };

  const salvar = async () => {
    if (!rascunho) return;
    setErro(null);
    setSalvando(true);
    try {
      const corpo = corpoDoRascunho(rascunho);
      const r = rascunho.id
        ? await api.put<{ pontos: Ponto[] }>(`/api/v1/admin/whatsapp/pontos-atencao/${rascunho.id}`, corpo)
        : await api.post<{ pontos: Ponto[] }>('/api/v1/admin/whatsapp/pontos-atencao', corpo);
      setPontos(r.pontos);
      setRascunho(null);
      setPrevia(null);
    } catch (e) {
      const corpo = e instanceof ApiError ? (e.response as { erro?: string } | undefined) : undefined;
      setErro(corpo?.erro ?? 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const alternarLigado = async (p: Ponto) => {
    setErro(null);
    try {
      const corpo = {
        tipo: tipoDoPonto(p),
        rotulo: p.rotulo,
        perguntaTexto: p.perguntaTexto ?? undefined,
        perguntaId: p.perguntaId,
        quando: p.quando,
        contemTexto: p.contemTexto ?? undefined,
        mostrarResposta: p.mostrarResposta,
        palavrasChave: tipoDoPonto(p) === 'relato' ? p.palavrasChave : undefined,
        gatilhoRegex: tipoDoPonto(p) === 'avancado' ? p.gatilhoRegex : undefined,
        ligado: !p.ligado,
      };
      const r = await api.put<{ pontos: Ponto[] }>(`/api/v1/admin/whatsapp/pontos-atencao/${p.id}`, corpo);
      setPontos(r.pontos);
    } catch {
      setErro('Não foi possível ligar/desligar.');
    }
  };

  const excluir = async (p: Ponto) => {
    const ok = await confirmar({
      title: 'Excluir este ponto de atenção',
      message: `“${p.rotulo}” deixa de aparecer nas próximas mensagens. As já enviadas não mudam.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      const r = await api.delete<{ pontos: Ponto[] }>(`/api/v1/admin/whatsapp/pontos-atencao/${p.id}`);
      setPontos(r.pontos);
    } catch {
      setErro('Não foi possível excluir.');
    }
  };

  return (
    <div className="space-y-3" data-testid="wa-pontos-atencao">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Pontos de atenção da mensagem de OS
          <HelpTip texto="Um ponto de atenção é uma linha extra que entra na mensagem da OS quando algo merece destaque — por exemplo, o técnico respondeu que precisa de orçamento. Você escolhe a pergunta do questionário numa lista (criou questionário novo no Auvo, as perguntas aparecem aqui sozinhas na primeira tarefa respondida), diz quando o ponto aparece e escreve o rótulo. A prévia mostra em quantas tarefas reais ele teria aparecido, antes de salvar." />
        </span>
        <Button size="sm" onClick={() => abrirEditor()} data-testid="wa-ponto-novo">
          Novo ponto de atenção
        </Button>
      </div>
      {erro && <Alert variant="danger">{erro}</Alert>}

      {pontos === null ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : (
        <div className="space-y-2">
          {pontos.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-line p-3 dark:border-line-dark"
              data-testid={`wa-ponto-${p.id}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink-1 dark:text-ink-dark-1">{p.rotulo}</span>
                {tipoDoPonto(p) === 'pergunta' && <Badge variant="info" size="sm">pergunta do questionário</Badge>}
                {tipoDoPonto(p) === 'relato' && <Badge variant="info" size="sm">palavra no relato</Badge>}
                {tipoDoPonto(p) === 'avancado' && <Badge variant="neutral" size="sm">modo avançado</Badge>}
                {p.mostrarResposta && <Badge variant="neutral" size="sm">mostra a resposta do técnico</Badge>}
                {!p.ligado && <Badge variant="warning" size="sm">desligado</Badge>}
              </div>
              <p className="mt-1 text-sm text-ink-2 dark:text-ink-dark-2">{fraseDoPonto(p)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Switch checked={p.ligado} label="Ligado" onChange={() => void alternarLigado(p)} />
                <Button size="sm" variant="secondary" onClick={() => abrirEditor(p)} data-testid={`wa-ponto-editar-${p.id}`}>
                  Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void excluir(p)} data-testid={`wa-ponto-excluir-${p.id}`}>
                  Excluir
                </Button>
              </div>
            </div>
          ))}
          {pontos.length === 0 && (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3">
              Nenhum ponto de atenção — as mensagens de OS saem sem a seção.
            </p>
          )}
        </div>
      )}

      {rascunho && (
        <div className="space-y-3 rounded-lg border border-line p-3 dark:border-line-dark" data-testid="wa-ponto-editor">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            {rascunho.id ? 'Editar ponto de atenção' : 'Novo ponto de atenção'}
          </p>

          <div>
            <label className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">O ponto observa</label>
            <Select
              value={rascunho.tipo === 'avancado' ? 'pergunta' : rascunho.tipo}
              onChange={(e) => setRascunho({ ...rascunho, tipo: e.target.value as Tipo })}
              data-testid="wa-ponto-tipo"
              className="max-w-xs"
            >
              <option value="pergunta">Uma pergunta do questionário</option>
              <option value="relato">Palavras no relato do técnico</option>
            </Select>
          </div>

          {rascunho.tipo === 'pergunta' && (
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                  A pergunta — como você a escreveu no Auvo
                </label>
                {rascunho.perguntaTexto ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-ink-1 dark:text-ink-dark-1">“{rascunho.perguntaTexto}”</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRascunho({ ...rascunho, perguntaTexto: '', perguntaId: null })}
                    >
                      Trocar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Input
                      value={buscaPergunta}
                      onChange={(e) => setBuscaPergunta(e.target.value)}
                      placeholder="Procurar pergunta…"
                      data-testid="wa-ponto-busca-pergunta"
                    />
                    <div className="max-h-48 space-y-1 overflow-y-auto">
                      {perguntasFiltradas.map((q) => (
                        <button
                          key={`${q.id ?? q.texto}`}
                          type="button"
                          className="block w-full rounded-md border border-line px-2 py-1.5 text-left text-sm hover:bg-surface-2 dark:border-line-dark dark:hover:bg-surface-dark-2"
                          onClick={() => setRascunho({ ...rascunho, perguntaTexto: q.texto, perguntaId: q.id })}
                          data-testid={`wa-ponto-pergunta-${q.id ?? q.texto}`}
                        >
                          <span className="text-ink-1 dark:text-ink-dark-1">{q.texto}</span>
                          <span className="block text-xs text-ink-3 dark:text-ink-dark-3">
                            respondida em {q.tarefas} tarefa{q.tarefas === 1 ? '' : 's'} · aparece em:{' '}
                            {q.tipos.slice(0, 3).join(' · ')}
                            {q.tipos.length > 3 ? ` e mais ${q.tipos.length - 3}` : ''}
                          </span>
                        </button>
                      ))}
                      {perguntasFiltradas.length === 0 && (
                        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
                          Nenhuma pergunta com esse texto. As perguntas vêm das tarefas do Auvo — um
                          questionário novo aparece aqui quando a primeira tarefa respondida chegar.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">O ponto aparece quando</label>
                <Select
                  value={rascunho.quando}
                  onChange={(e) => setRascunho({ ...rascunho, quando: e.target.value as Rascunho['quando'] })}
                  data-testid="wa-ponto-quando"
                  className="max-w-xs"
                >
                  <option value="sim">A resposta for “Sim”</option>
                  <option value="qualquer">O técnico responder qualquer coisa</option>
                  <option value="contem">A resposta contiver uma palavra</option>
                </Select>
                {rascunho.quando === 'sim' && (
                  <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                    Vale do jeito que o técnico digitar: sim, SIM, s, “sim.”…
                  </p>
                )}
                {rascunho.quando === 'contem' && (
                  <Input
                    className="mt-1 max-w-xs"
                    value={rascunho.contemTexto}
                    onChange={(e) => setRascunho({ ...rascunho, contemTexto: e.target.value })}
                    placeholder="A palavra — ex.: energia"
                    data-testid="wa-ponto-contem"
                  />
                )}
              </div>
            </div>
          )}

          {rascunho.tipo === 'relato' && (
            <div>
              <label className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                Palavras que procuram no relato (separe por vírgula)
              </label>
              <Input
                value={rascunho.palavras}
                onChange={(e) => setRascunho({ ...rascunho, palavras: e.target.value })}
                placeholder="Ex.: poda, serralheria"
                data-testid="wa-ponto-palavras"
              />
              <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                Quando o relato mencionar alguma, as frases dele entram na mensagem.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">O rótulo que sai na mensagem</label>
            <Input
              value={rascunho.rotulo}
              onChange={(e) => setRascunho({ ...rascunho, rotulo: e.target.value })}
              placeholder="Ex.: Necessidade de orçamento"
              data-testid="wa-ponto-rotulo"
            />
          </div>

          {rascunho.tipo === 'pergunta' && (
            <div>
              <Switch
                checked={rascunho.mostrarResposta}
                label="Mostrar também a resposta do técnico"
                onChange={(e) => setRascunho({ ...rascunho, mostrarResposta: e.target.checked })}
                data-testid="wa-ponto-mostrar-resposta"
              />
              {rascunho.mostrarResposta && (
                <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                  ⚠️ O texto vem do técnico e sai como ele escreveu — sem revisão.
                </p>
              )}
            </div>
          )}

          {/* O modo avançado: escondido, não apagado. */}
          <div>
            <button
              type="button"
              className="text-xs text-ink-3 underline dark:text-ink-dark-3"
              onClick={() => setAvancadoAberto(!avancadoAberto)}
              data-testid="wa-ponto-avancado-abrir"
            >
              {avancadoAberto ? 'Fechar o modo avançado' : 'Modo avançado…'}
            </button>
            {avancadoAberto && (
              <div className="mt-2 space-y-1">
                <label className="block text-xs text-ink-3 dark:text-ink-dark-3">
                  Expressão técnica que encontra a pergunta (regex) — só quando a lista não resolver
                </label>
                <Input
                  value={rascunho.gatilhoRegex}
                  className="font-mono"
                  onChange={(e) => setRascunho({ ...rascunho, gatilhoRegex: e.target.value, tipo: 'avancado' })}
                  data-testid="wa-ponto-regex"
                />
                {rascunho.tipo === 'avancado' && (
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                    Este ponto vai usar a expressão, não a pergunta escolhida.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              loading={carregandoPrevia}
              disabled={!prontoParaSalvar(rascunho)}
              onClick={() => void verPrevia()}
              data-testid="wa-ponto-previa"
            >
              Ver a prévia nas tarefas reais
            </Button>
            <Button
              size="sm"
              loading={salvando}
              disabled={!prontoParaSalvar(rascunho)}
              onClick={() => void salvar()}
              data-testid="wa-ponto-salvar"
            >
              Salvar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setRascunho(null); setPrevia(null); }}>
              Cancelar
            </Button>
          </div>

          {previa && (
            <div className="rounded-md border border-line p-2 text-sm dark:border-line-dark" data-testid="wa-ponto-previa-resultado">
              {previa.dispararia === 0 ? (
                <Alert variant="warning">
                  Do jeito que está, este ponto <strong>nunca teria aparecido</strong> nas{' '}
                  {previa.tarefasNoEspelho} tarefas dos últimos 30 dias. Confira a pergunta e o
                  “quando” antes de salvar.
                </Alert>
              ) : (
                <>
                  <p className="text-ink-2 dark:text-ink-dark-2">
                    Teria aparecido em <strong>{previa.dispararia}</strong> das {previa.tarefasNoEspelho}{' '}
                    tarefas dos últimos 30 dias. Exemplo real (OS {previa.exemplo?.taskId}):
                  </p>
                  <pre className="mt-1 whitespace-pre-wrap rounded bg-surface-2 p-2 text-xs text-ink-1 dark:bg-surface-dark-2 dark:text-ink-dark-1">
                    {previa.exemplo?.linha}
                  </pre>
                  <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                    Para ver a mensagem inteira com este ponto, use a aba “Testar OS” com o número de
                    uma tarefa.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
